import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const emptyCaptcha = { id: "", prompt: "", answer: "", expiresAt: 0 };
const retryDelays = [500, 1500];

const pause = (milliseconds, signal) => new Promise((resolve, reject) => {
  const abort = () => { clearTimeout(timer); reject(new Error("Cancelled")); };
  const timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, milliseconds);
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) abort();
});

async function requestCaptcha(signal) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const { data } = await api.get("/auth/captcha", {
        signal, timeout: 8000, params: { _: Date.now() },
      });
      if (!data || typeof data.captcha_id !== "string" || !data.captcha_id ||
          typeof data.prompt !== "string" || !data.prompt ||
          !Number.isFinite(data.expires_in) || data.expires_in <= 5) {
        throw new Error("Invalid captcha response");
      }
      return { id: data.captcha_id, prompt: data.prompt, answer: "", expiresAt: Date.now() + data.expires_in * 1000 };
    } catch (error) {
      const status = error.response?.status;
      if (signal.aborted || attempt === 2 || (status && ![502, 503, 504].includes(status))) throw error;
      await pause(retryDelays[attempt], signal);
    }
  }
}

export const useSignupCaptcha = (paused = false) => {
  const [captcha, setCaptcha] = useState({ ...emptyCaptcha, loading: true, error: "", notice: "" });
  const activeRequest = useRef(null);
  const sequence = useRef(0);

  const loadCaptcha = useCallback(async (notice = "") => {
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    const current = ++sequence.current;
    setCaptcha({ ...emptyCaptcha, loading: true, error: "", notice });
    try {
      const next = await requestCaptcha(controller.signal);
      if (!controller.signal.aborted && current === sequence.current) {
        setCaptcha({ ...next, loading: false, error: "", notice });
      }
    } catch {
      if (!controller.signal.aborted && current === sequence.current) {
        setCaptcha({ ...emptyCaptcha, loading: false, notice: "", error: "We couldn’t load the human check. Your details are still here." });
      }
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
    return () => { sequence.current += 1; activeRequest.current?.abort(); };
  }, [loadCaptcha]);

  useEffect(() => {
    if (paused || !captcha.id || captcha.loading) return;
    const refreshIfExpired = () => {
      if (Date.now() >= captcha.expiresAt - 5000) {
        loadCaptcha("A fresh question is ready. Please answer it to continue.");
      }
    };
    const timer = setTimeout(refreshIfExpired, Math.max(0, captcha.expiresAt - Date.now() - 5000));
    window.addEventListener("focus", refreshIfExpired);
    document.addEventListener("visibilitychange", refreshIfExpired);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", refreshIfExpired);
      document.removeEventListener("visibilitychange", refreshIfExpired);
    };
  }, [captcha.id, captcha.expiresAt, captcha.loading, paused, loadCaptcha]);

  const setAnswer = useCallback((answer) => {
    setCaptcha((current) => ({ ...current, answer: answer.replace(/[^0-9]/g, "") }));
  }, []);

  return { captcha, loadCaptcha, setAnswer };
};